import { z } from 'zod'

/**
 * `business.json` — the publishing input for one studio.
 *
 * This file is the contract between the `tenant:*` scripts and the
 * application. The database is the runtime source; this is what gets pushed
 * into it. Both sides validate with the same schema, so a config that passes
 * `tenant:validate` cannot fail differently at publish time.
 */
const hexColor = z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Expected #RRGGBB')

const timeOfDay = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Expected HH:MM')

export const tenantSlugPattern = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/

export const workingHourSchema = z
  .object({
    /** 0 = Sunday, matching JavaScript getDay() and the database. */
    weekday: z.number().int().min(0).max(6),
    opensAt: timeOfDay,
    closesAt: timeOfDay,
  })
  .refine((day) => day.closesAt > day.opensAt, {
    message: 'closesAt must be later than opensAt',
  })

export const tenantServiceSchema = z
  .object({
    slug: z.string().regex(tenantSlugPattern),
    name: z.string().min(1),
    summary: z.string().min(1),
    /** Whole roubles in the config; stored as minor units in the database. */
    priceFromRub: z.number().int().positive(),
    durationMinMinutes: z.number().int().positive(),
    durationMaxMinutes: z.number().int().positive(),
    /** Preparation time after this service, before the next car. */
    bufferMinutes: z.number().int().min(0).max(240).default(15),
    /** Omitted preserves legacy handover/multi-day semantics on republish. */
    allowOvernight: z.boolean().optional(),
    includes: z.array(z.string().min(1)).default([]),
    notes: z.array(z.string().min(1)).default([]),
    /** Names of the resources that can perform it. Empty means any. */
    resources: z.array(z.string().min(1)).default([]),
    sortOrder: z.number().int().default(0),
  })
  .refine((service) => service.durationMaxMinutes >= service.durationMinMinutes, {
    message: 'durationMaxMinutes must be at least durationMinMinutes',
  })

export const tenantResourceSchema = z.object({
  name: z.string().min(1),
  kind: z.enum(['bay', 'mechanic', 'equipment']).default('bay'),
  sortOrder: z.number().int().default(0),
})

export const tenantInfoCardSchema = z.object({
  title: z.string().min(1).max(40),
  body: z.string().min(1).max(200),
})

export const tenantConfigSchema = z.object({
  slug: z.string().regex(tenantSlugPattern).min(2).max(63),
  /**
   * `preview` until the studio's settings have been checked. A preview tenant
   * carries demo content, is not publicly listed and sends no notifications.
   */
  status: z.enum(['preview', 'live']).default('preview'),
  /** Marks content that is illustrative rather than the studio's own. */
  isDemoContent: z.boolean().default(false),

  name: z.string().min(1),
  tagline: z.string().trim().default(''),
  about: z.string().trim().default(''),

  address: z.string().min(1),
  addressNote: z.string().default(''),
  phone: z.string().regex(/^\+[0-9]{10,15}$/, 'Expected E.164, e.g. +74951234567'),
  phoneDisplay: z.string().min(1),

  /** Accent seed. Astryx derives the rest of the palette, contrast included. */
  accent: z.object({ light: hexColor, dark: hexColor }),
  timezone: z.string().min(1),

  workingHours: z.array(workingHourSchema).max(7),
  resources: z.array(tenantResourceSchema).min(1),
  services: z.array(tenantServiceSchema).min(1),
  infoCards: z.array(tenantInfoCardSchema).max(3).default([]),
})

export type TenantConfig = z.infer<typeof tenantConfigSchema>
export type TenantService = z.infer<typeof tenantServiceSchema>
export type TenantResource = z.infer<typeof tenantResourceSchema>

/** Every weekday appearing at most once, and every named resource existing. */
export function findConfigProblems(config: TenantConfig): string[] {
  const problems: string[] = []

  const weekdays = config.workingHours.map((day) => day.weekday)
  if (new Set(weekdays).size !== weekdays.length) {
    problems.push('workingHours lists the same weekday twice')
  }
  if (config.workingHours.length === 0) {
    problems.push('workingHours is empty: the studio would never accept a car')
  }

  const resourceNames = new Set(config.resources.map((resource) => resource.name))
  if (resourceNames.size !== config.resources.length) {
    problems.push('two resources share a name')
  }

  const serviceSlugs = config.services.map((service) => service.slug)
  if (new Set(serviceSlugs).size !== serviceSlugs.length) {
    problems.push('two services share a slug')
  }

  for (const service of config.services) {
    for (const name of service.resources) {
      if (!resourceNames.has(name)) {
        problems.push(`service "${service.slug}" names an unknown resource "${name}"`)
      }
    }
  }

  return problems
}
